// PR-29: mobile session-token storage.
//
// The native app authenticates with a bearer token, not a cookie — a
// Capacitor WebView can't carry the session cookie cross-origin. The raw
// token IS the session secret; it's persisted via @capacitor/preferences
// (UserDefaults-backed, survives app restarts) and mirrored in a
// module-level variable so `apiFetch` can attach the Authorization header
// synchronously, without an async storage read on every request.
//
// `loadMobileToken()` must run once at boot (mobile.tsx) before the first
// apiFetch. All no-ops on web — `getMobileToken()` just returns null.
import { Preferences } from '@capacitor/preferences'
import { isCapacitor } from './platform'

const TOKEN_KEY = 'draftlize_mobile_token'

let cached: string | null = null

/** Load the persisted token into memory. Call once at boot, before the
 *  first apiFetch (CoreProvider's /api/me must carry the header). */
export async function loadMobileToken(): Promise<void> {
  if (!isCapacitor()) return
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY })
    cached = value ?? null
  } catch {
    cached = null
  }
}

/** The current bearer token, or null. Synchronous — reads the cache. */
export function getMobileToken(): string | null {
  return cached
}

/** Persist a freshly minted token after a successful login. */
export async function setMobileToken(token: string): Promise<void> {
  cached = token
  if (!isCapacitor()) return
  await Preferences.set({ key: TOKEN_KEY, value: token })
}

/** Drop the token (logout, or a 401 invalidated the session). */
export async function clearMobileToken(): Promise<void> {
  cached = null
  if (!isCapacitor()) return
  await Preferences.remove({ key: TOKEN_KEY })
}
